import { endOfDay, isAfter, isBefore, startOfDay } from 'date-fns'
import { Employer, InterimAgency, Organization } from '../api/organizations'
import {
  User,
  UserAssociation,
  UserRole,
  UserRoleKind,
  ContractualScheduleEntry,
  ContractualWeeklySchedule,
} from '../api/users'
import type { MissionScheduleTemplate } from '../api/missions'
import useStore from '../store'
import { localDate } from './date'
import { compareDocsById } from './documents'
import { WorkerRole } from '../../../backend/src/services/resources/users/user.model'

interface UserLabelData {
  firstName: string
  lastName: string
  appAccess?: User['appAccess']
  internalRef?: string
  associations?: User['associations']
}

export const getUserLabel = (
  user: UserLabelData,
  options?: { hideOfflineInfo?: boolean },
): string => {
  const currentOrganization = useStore.getState().session.currentOrganization
  const internalRef =
    user.internalRef ||
    (user.associations &&
      getWorkerAndOrganizationInternalRef({ associations: user.associations }, currentOrganization))

  const offlineStatus = user.appAccess === 'none' && !options?.hideOfflineInfo ? ' - offline' : ''
  let label = `${user.lastName} ${user.firstName}${offlineStatus}`
  if (internalRef) label += ` (${internalRef})`
  return label
}

export const getWorkerAndOrganizationAssociation = (
  worker: Pick<User, 'associations'>,
  organization: Organization | string,
): UserAssociation | undefined =>
  worker.associations.find(association => compareDocsById(association.organization, organization))

export const getWorkerLaborCostCoefficientKind = (
  worker: Pick<User, 'associations'>,
  interimAgency: InterimAgency | string,
  employer: Employer,
): 'management' | 'delegation' | null => {
  const association = getWorkerAndOrganizationAssociation(worker, interimAgency)
  if (!association) return null

  const laborCostCoefficientData = association.laborCostCoefficients.find(c =>
    compareDocsById(c.employer, employer),
  )

  return laborCostCoefficientData?.kind === 'management' ? 'management' : 'delegation'
}

export const getUserRole = (
  user: User,
  roleKind: UserRoleKind,
  organizationId?: string,
): UserRole | undefined => {
  if ((roleKind === 'employerMember' || roleKind === 'interimAgencyMember') && !organizationId)
    return
  return user.roles.find(
    role =>
      role.kind === roleKind &&
      (!organizationId || compareDocsById(organizationId, role.organization)),
  )
}

export const getWorkerAndOrganizationInternalRef = (
  worker: Pick<User, 'associations'>,
  organization?: Organization,
): string | undefined => {
  const association = organization && getWorkerAndOrganizationAssociation(worker, organization)
  return association?.internalRef || ''
}
export const getWorkerAvailabilityForOrganization = (
  user: Pick<User, 'associations'>,
  organization: Organization,
): UserAssociation['availability'] | undefined => {
  const association = getWorkerAndOrganizationAssociation(user, organization)
  return association?.availability
}

const checkWorkerIsAvailableForOrganizationInPeriod = (
  start: Date,
  end: Date,
  user: Pick<User, 'associations'>,
  organization: Organization,
): boolean => {
  const availability = getWorkerAvailabilityForOrganization(user, organization)
  return Boolean(!availability || checkAvailibilityInPeriod(start, end, availability))
}

export const checkAvailibilityInPeriod = (
  start: Date,
  end: Date,
  availability: UserAssociation['availability'],
): boolean => {
  const { absentFrom } = availability || {}
  let { absentTo } = availability || {}
  if (!absentFrom) return true
  if (!absentTo) absentTo = new Date(3000, 1, 1)

  if (isBefore(new Date(absentFrom), end) && isAfter(new Date(absentTo), start)) return false

  return true
}

export const workerIsIsAvailableForOrganizationAndDay = (
  date: Date,
  user: Pick<User, 'associations'>,
  organization: Organization,
): boolean =>
  checkWorkerIsAvailableForOrganizationInPeriod(
    startOfDay(date),
    endOfDay(date),
    user,
    organization,
  )

export const getWorkerAbsentLabelForOrganization = (
  user: User,
  organization: Organization,
): string => {
  const { absentFrom, absentTo } = getWorkerAvailabilityForOrganization(user, organization) || {}
  let label = `${getUserLabel(user)} s'est renseigné comme indisponible du 
  ${absentFrom && localDate(absentFrom)}`
  if (absentTo) label = `${label} au ${localDate(absentTo)}`
  else label = `${label} et pour une durée indeterminée`
  return label
}

export const getWorkerAbsentLabel = (
  availability: UserAssociation['availability'],
  user: UserLabelData,
  short?: boolean,
): string => {
  const { absentFrom, absentTo } = availability
  if (!absentFrom) return ''
  let label = short
    ? `Du ${absentFrom && localDate(absentFrom)}`
    : `${getUserLabel(user)} s'est renseigné comme indisponible du 
  ${absentFrom && localDate(absentFrom)}`
  if (absentTo) label = `${label} au ${localDate(absentTo)}`
  else label = short ? `${label} au indeterminé` : `${label} et pour une durée indeterminée`
  return label
}

export const getWorkerRoleOrAssociationStatus = (
  user: User,
  roleKind: UserRoleKind,
  organization?: Organization,
): UserRole['status'] | undefined => {
  if (roleKind === 'worker' && organization) {
    const association = getWorkerAndOrganizationAssociation(user, organization)
    return association?.status
  }
  return getUserRole(user, roleKind, organization?._id)?.status
}

export const getWorkerEmployerRelationData = (
  user: User,
  organization: Organization,
): WorkerRole['employerRelations'][0] | undefined => {
  const workerRole = getUserRole(user, 'worker') as unknown as WorkerRole | undefined
  return workerRole?.employerRelations.find(relation =>
    compareDocsById(relation.employer, organization),
  )
}

export const workerIsEmployerCollaborationInactive = (
  user: User,
  organization: Organization,
): boolean => {
  const employerRelation = getWorkerEmployerRelationData(user, organization)
  return employerRelation?.collaboration?.isInactive || false
}

/**
 * Get the active contractual schedule entry for an association
 * The active entry is the one with effectiveTo === null and the most recent effectiveFrom
 * If no active entry exists, returns the most recent entry
 * @param association - User association containing contractual schedules
 * @param date - Optional date to get the entry valid at that specific date (defaults to today)
 * @returns The active or most recent contractual schedule entry, or undefined if none exists
 */
export const getActiveContractualSchedule = (
  association: UserAssociation,
  date?: Date,
): ContractualScheduleEntry | undefined => {
  if (!association.contractualSchedules || association.contractualSchedules.length === 0) {
    return undefined
  }

  const targetDate = date || new Date()

  // Find entries valid at the target date
  const validEntries = association.contractualSchedules.filter(entry => {
    const effectiveFrom = new Date(entry.effectiveFrom)
    const effectiveTo = entry.effectiveTo ? new Date(entry.effectiveTo) : null

    // Entry is valid if:
    // - effectiveFrom <= targetDate
    // - effectiveTo is null OR effectiveTo >= targetDate
    return effectiveFrom <= targetDate && (effectiveTo === null || effectiveTo >= targetDate)
  })

  if (validEntries.length === 0) {
    // No valid entry at target date, return the most recent entry
    return association.contractualSchedules.sort(
      (a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime(),
    )[0]
  }

  // Among valid entries, prefer the active one (effectiveTo === null)
  const activeEntry = validEntries.find(entry => entry.effectiveTo === null)

  if (activeEntry) {
    return activeEntry
  }

  // If no active entry, return the most recent valid entry
  return validEntries.sort(
    (a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime(),
  )[0]
}

/**
 * Convertit une date en nom de jour pour accéder au contractual schedule
 * @param date - Date à convertir
 * @returns Nom du jour (monday, tuesday, etc.)
 */
export const getDayNameFromDate = (date: Date): keyof ContractualWeeklySchedule => {
  const dayOfWeek = date.getDay() // 0 = dimanche, 1 = lundi, ..., 6 = samedi
  // Convertir en monday-based (1 = lundi, 7 = dimanche)
  const mondayBasedDay = dayOfWeek === 0 ? 7 : dayOfWeek

  const dayNames: (keyof ContractualWeeklySchedule)[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]

  return dayNames[mondayBasedDay - 1] as keyof ContractualWeeklySchedule
}

/**
 * Check if a worker has at least one contractual schedule entry with schedule (non-null)
 * @param association - User association containing contractual schedules
 * @returns true if at least one entry has schedule !== null && schedule !== undefined
 */
export const hasContractualScheduleWithTimeSlots = (association?: UserAssociation): boolean => {
  if (!association?.contractualSchedules || association.contractualSchedules.length === 0) {
    return false
  }

  return association.contractualSchedules.some(
    entry => entry.schedule !== null && entry.schedule !== undefined,
  )
}

/** Day keys in ContractualWeeklySchedule, in order 0 (Monday) to 6 (Sunday). */
const CONTRACTUAL_DAY_KEYS: (keyof ContractualWeeklySchedule)[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

/**
 * Converts an active contractual schedule entry to a mission schedule template.
 * For each day, if there are multiple time slots, uses the start of the first and end of the last.
 *
 * @param entry - Contractual schedule entry with schedule (weekly time slots)
 * @returns MissionScheduleTemplate or null if entry has no schedule
 */
export const contractualScheduleEntryToMissionScheduleTemplate = (
  entry: ContractualScheduleEntry,
): MissionScheduleTemplate | null => {
  if (!entry.schedule) return null

  const days: Partial<Record<0 | 1 | 2 | 3 | 4 | 5 | 6, { startHour: number; endHour: number }>> =
    {}

  for (let dayIndex = 0; dayIndex < CONTRACTUAL_DAY_KEYS.length; dayIndex++) {
    const dayKey = CONTRACTUAL_DAY_KEYS[dayIndex] as keyof ContractualWeeklySchedule
    const daySchedule = entry.schedule[dayKey]
    const slots = daySchedule?.timeSlots?.filter(Boolean) ?? []
    if (slots.length === 0) continue

    const startHour = Math.min(...slots.map(s => s.startHour))
    const endHour = Math.max(...slots.map(s => s.endHour))
    days[dayIndex as 0 | 1 | 2 | 3 | 4 | 5 | 6] = { startHour, endHour }
  }

  return {
    start: new Date(),
    end: null,
    weekPatterns: [{ days }],
  }
}
